'use client';

import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';

import {useDotButton} from '@/components/carousel/hooks/useDotButton';
import {GridDetails} from '@/components/content-blocks/link-list-selection/grid-details';
import {LinkCard} from '@/components/content-blocks/link-list-selection/link-card';
import type {LinkListSelectionFragment} from '@/components/content-blocks/link-list-selection/query';
import {usePrefersReducedMotion} from '@/modules/foundation/hooks/usePrefersReducedMotion';

type Props = {
	linkItems: LinkListSelectionFragment['linkItems'];
	headingLevel?: 2 | 3;
};

export const GridCarousel = ({linkItems, headingLevel}: Props) => {
	const prefersReducedMotion = usePrefersReducedMotion();

	const [emblaRef, emblaApi] = useEmblaCarousel({
		duration: prefersReducedMotion ? 0 : 20,
		skipSnaps: true,
		align: 'start',
	});

	const {selectedIndex, scrollSnaps, onDotButtonClick} = useDotButton(emblaApi);

	if (!linkItems?.length) {
		return null;
	}

	const firstLinkItem = linkItems[0];
	const remainingLinkItems = linkItems.slice(1);

	return (
		<div>
			{linkItems.length <= 3 ? (
				// If we have fewer than 4 links, there's enough space without the carousel.
				// Just fall back to using the grid to keep things clean:
				<GridDetails linkItems={linkItems} headingLevel={headingLevel} />
			) : (
				<div className={clsx('flex', 'flex-col', 'gap-4', 'md:gap-8')}>
					<LinkCard
						linkItem={firstLinkItem}
						forceAspectRatio="4/3"
						className={clsx('md:hidden')}
					/>
					{/* Embla viewport */}

					<div ref={emblaRef}>
						{/* Embla container */}
						<div
							className={clsx(
								'flex',
								'touch-pan-y',
								'touch-pinch-zoom',
								'gap-4',
								'md:gap-8',
							)}
						>
							<LinkCard
								linkItem={firstLinkItem}
								className={clsx(
									'max-md:hidden',
									'shrink-0',
									'grow-0',
									'basis-[min(42%,_350px)]',
									'md:basis-[442px]',
								)}
							/>

							{remainingLinkItems.map((linkItem) => (
								<LinkCard
									key={linkItem._key}
									linkItem={linkItem}
									className={clsx(
										'shrink-0',
										'grow-0',
										'basis-[min(42%,_350px)]',
										'md:basis-[442px]',
									)}
								/>
							))}
						</div>
					</div>

					{scrollSnaps.length > 1 && (
						<div
							className={clsx(
								'flex',
								'justify-center',
								'gap-4',
								'flex-wrap',
								'items-center',
							)}
						>
							{scrollSnaps.map((_, index) => (
								<button
									type="button"
									key={index}
									// eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
									onClick={() => onDotButtonClick(index)}
									aria-label={`Bilde ${index + 1}`}
									className={clsx(
										'relative',
										'h-1',
										'w-8',
										'bg-dypgronn-industri',
										'opacity-40',
										'before:absolute',
										'before:inset-[-8px]',
										"before:content-['']",
										'hover:opacity-100',
										'focus:opacity-100',
										'disabled:opacity-30',
										selectedIndex === index ? 'opacity-100' : '',
										'cursor-pointer',
									)}
								/>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
};
